import { type FC, useRef } from 'react';

import useMarketo from 'molecules/Form/utils/useMarketo';
import BasicForm from 'molecules/Form/variants/Basic';
import SingleInputForm from 'molecules/Form/variants/SingleInput';

export type FormLayout = 'inline' | 'stacked';

type FormVariant = 'singleInput' | 'basic';

type FormSwitchProps = FormProps & {
  variant: FormVariant;
  layout?: FormLayout;
  successMessageClassName?: string;
};

const Form: FC<FormSwitchProps> = ({ variant, layout, successMessageClassName, ...props }) => {
  switch (variant) {
    case 'singleInput':
      return <SingleInputForm {...props} layout={layout} successMessageClassName={successMessageClassName} />;
    case 'basic':
      return <BasicForm {...props} />;
    default:
      throw new Error(`Variant of type ${variant} is not supported`);
  }
};

type MarketoProps = {
  formData: FormReference;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback?: (ctx?: { values?: Record<string, any> }) => boolean | void;
  layout?: FormLayout;
  lockedContentPageId?: string;
  extraHiddenFields?: Record<string, string>;
  successMessageClassName?: string;
};

const MarketoForm: FC<MarketoProps> = ({
  formData,
  callback,
  layout = 'inline',
  lockedContentPageId,
  extraHiddenFields,
  successMessageClassName,
}) => {
  const formId = formData?.formId?.trim() || '';
  const variant = (formData?.variant as FormVariant) || 'basic';
  const legalText = formData?.legalText;
  const labelHeading = formData?.labelHeading;
  const successMessage = formData?.successMessage;
  const featuredImage = formData?.featuredImage;
  const icon = formData?.icon;
  const id = `mktoForm_${formId}`;

  const rootRef = useRef<HTMLDivElement>(null);
  const formState = useMarketo({
    formId,
    callback,
    rootRef,
    rootMargin: '1200px',
    lockedContentPageId,
    icon,
    extraHiddenFields,
  });

  return (
    <div ref={rootRef} data-mkto-root id="mkto-form-block">
      <Form
        id={id}
        variant={variant}
        successMessage={successMessage}
        legalText={legalText}
        labelHeading={labelHeading}
        featuredImage={featuredImage}
        layout={layout}
        successMessageClassName={successMessageClassName}
        {...formState}
      />
    </div>
  );
};

export default MarketoForm;
